/* tmc-cart.js — the <cart-drawer> element behind snippets/tmc-cart-drawer.liquid. NW-34, issue #163,
   board family `cart-drawer` (docs/nw/boards/cart-drawer.md).
   Entry points other code relies on: open(), close(), renderContents(json), getSectionsToRender(), setActiveElement(el)
   (Dawn's product-form.js and assets/tmc-patient-form.js call these). Every refresh re-reads /cart.js and draws the
   lines from JSON. Events: listens for cart:open and cart:added on document; dispatches cart:updated {item_count}.

   Rules this file exists to hold:
   - The return satchel is a REAL $0.00 line (SKU SHIP-FEE). It is drawn at its real price, never re-priced and
     never hidden, and no shipping amount is added to the subtotal — standard shipping is a Shopify rate applied
     at checkout (docs/nw/boards/cart-drawer.md, CD-27).
   - The satchel never stands alone (NW-35). It is there to make the rate apply to tests, so when the last test
     goes the satchel goes with it, and a cart holding just a satchel is emptied on the next read.
   - A line added through an add is marked ADDED for two seconds from the read that showed it as new, then settles
     (board family `cart-states`, state B). The clock is per line, so another read of the cart inside the window
     (a cart:open, a Remove, the accessory's own add) neither wipes the mark nor starts a new one.
   - Test lines carry the five frozen line-item properties and a Remove control. There is no quantity control on a
     test line: docs/form-contract.md §2 is what stops one patient's details reaching two physical kits. */
(function () {
  if (customElements.get('cart-drawer')) return;

  /* The five frozen properties, in docs/form-contract.md §1 order. */
  var PROP_KEYS = ['Patient Name', 'Patient DOB', 'Patient Gender', 'Doctor Clinic', 'Doctor Name'];

  var SETTLE_MS = 2000;

  var ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ENTITIES[c]; }); }
  function money(cents) {
    var n = (Math.round(cents) / 100).toFixed(2).split('.');
    return '$' + n[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + n[1];
  }
  /* Matches Liquid's money_without_trailing_zeros: the boards price tests and the subtotal whole ($370, $670)
     and the satchel to the cent ($0.00). */
  function moneyShort(cents) { return money(cents).replace(/\.00$/, ''); }

  var CHIP = '<span class="tmc-cart__chip"><svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.4 5 8.9l4.5-5.3"/></svg>Added</span>';

  class TMCCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;
      this.panel = this.querySelector('[data-cart-panel]');
      this.lines = this.querySelector('[data-cart-lines]');
      this.shipFeeVariant = parseInt(this.getAttribute('data-ship-fee-variant'), 10);
      try { this.lineMap = JSON.parse(this.querySelector('[data-cart-lines-map]').textContent) || {}; } catch (e) { this.lineMap = {}; }
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeElement = null;
      /* Lines already on the page are not new. A line that arrives through an add is the one marked ADDED. `known` is
         every line the last render drew; `marks` maps a new line's key to the time its ADDED mark runs out. */
      this.known = {};
      this.marks = {};
      var known = this.known;
      this.lines.querySelectorAll('[data-line-key]').forEach(function (li) { known[li.getAttribute('data-line-key')] = true; });

      var self = this;
      this.querySelector('[data-cart-overlay]').addEventListener('click', function () { self.close(); });
      this.querySelectorAll('[data-cart-close]').forEach(function (b) { b.addEventListener('click', function () { self.close(); }); });
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); self.close(); return; }
        if (e.key === 'Tab') self.trap(e);
      });
      this.lines.addEventListener('click', function (e) {
        var b = e.target.closest('[data-cart-remove]');
        if (!b) return;
        var li = b.closest('[data-line-key]');
        if (li) self.remove(li.getAttribute('data-line-key'));
      });
      this.drag();

      document.addEventListener('cart:open', function () { self.refresh().then(function () { self.open(); }); });
      document.addEventListener('cart:added', function () { self.refresh(true).then(function () { self.open(); }); });
    }

    /* Dawn's product-form.js contract. Sections are not needed; cart-icon-bubble still exists, so it is harmless. */
    getSectionsToRender() { return [{ id: 'cart-icon-bubble' }]; }
    setActiveElement(el) { this.activeElement = el; }
    renderContents(parsedState) {
      var self = this;
      if (parsedState && parsedState.items && typeof parsedState.item_count === 'number' && !parsedState.sections) {
        this.render(parsedState, true); this.open(); return;
      }
      this.refresh(true).then(function () { self.open(); });
    }

    open() {
      if (this.classList.contains('is-open')) return;
      if (!this.activeElement) this.activeElement = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('open', '');
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      var focus = this.querySelector('[data-cart-close]');
      var self = this;
      setTimeout(function () { (focus || self.panel).focus(); }, this.reduce.matches ? 0 : 60);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.removeAttribute('open');
      document.documentElement.style.overflow = this.savedOverflow || '';
      var el = this.activeElement; this.activeElement = null;
      if (el && typeof el.focus === 'function') el.focus();
    }
    trap(e) {
      var f = Array.prototype.filter.call(this.panel.querySelectorAll('a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])'), function (x) { return x.offsetParent !== null; });
      if (!f.length) return;
      var a = f[0], z = f[f.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    }

    /* Mobile sheet: dragging the handle down past 80px closes it. */
    drag() {
      var handle = this.querySelector('[data-cart-handle]'), panel = this.panel, self = this, startY = 0, dy = 0, on = false;
      if (!handle) return;
      handle.addEventListener('pointerdown', function (e) {
        on = true; dy = 0; startY = e.clientY; panel.classList.add('is-dragging'); handle.setPointerCapture(e.pointerId);
      });
      handle.addEventListener('pointermove', function (e) {
        if (!on) return;
        dy = Math.max(0, e.clientY - startY);
        panel.style.transform = 'translateY(' + dy + 'px)';
      });
      function end() {
        if (!on) return;
        on = false; panel.classList.remove('is-dragging'); panel.style.transform = '';
        if (dy > 80) self.close();
      }
      handle.addEventListener('pointerup', end);
      handle.addEventListener('pointercancel', end);
    }

    isSatchel(item) { return item.variant_id === this.shipFeeVariant || item.sku === 'SHIP-FEE'; }

    fetchJSON(url, init) {
      return fetch(url, init).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); });
    }
    post(url, body) {
      return this.fetchJSON(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
    }
    /* `fresh` is true when the read follows an add, so the lines that are new to the drawer get their ADDED mark. */
    refresh(fresh) {
      var self = this;
      return this.fetchJSON('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (cart) {
          self.render(cart, fresh);
          return self.tidy(cart);
        })
        .catch(function () { self.error('Your cart could not be loaded. Please try again.'); });
    }

    /* Tidying a lone satchel is a courtesy, not something the patient asked for: if the write fails they still have the
       empty cart they can see, and the next read of the cart tries again. Never rejects; hands back the cart it was given. */
    tidy(cart) {
      return this.dropLoneSatchel(cart).then(function () { return cart; }, function () { return cart; });
    }

    /* The board flag "ALL REMOVE last test: not specified whether the satchel line disappears" is settled here (NW-35):
       it does. The satchel is a $0.00 line that makes the Standard shipping rate apply to the tests, so with no test
       in the cart it would leave a checkout that ships nothing and still asks for the $27. Hands back the cart it
       ends on, so a caller can chain on it. */
    dropLoneSatchel(cart) {
      var self = this, items = (cart && cart.items) || [];
      if (!items.length || !items.every(function (i) { return self.isSatchel(i); })) return new Promise(function (done) { done(cart); });
      return this.post('/cart/change.js', { id: items[0].key, quantity: 0 })
        .then(function (next) { self.render(next); return self.dropLoneSatchel(next); });
    }

    remove(key) {
      var self = this;
      /* `is-busy` stops the pointer, not the keyboard: Enter on a focused Remove still fires, so a second press is ignored here. */
      if (this.classList.contains('is-busy')) return null;
      this.classList.add('is-busy');
      this.error('');
      return this.post('/cart/change.js', { id: key, quantity: 0 })
        .then(function (cart) { self.render(cart); return self.tidy(cart); })
        .catch(function (err) { self.error((err && (err.description || err.message)) || 'That change could not be made. Please try again.'); })
        .then(function () { self.classList.remove('is-busy'); });
    }
    error(msg) {
      var el = this.querySelector('[data-cart-error]');
      el.textContent = msg || '';
      el.classList.toggle('is-on', !!msg);
    }

    propsHTML(item) {
      var props = item.properties || {}, rows = '';
      PROP_KEYS.forEach(function (key) {
        var value = props[key];
        if (value == null || value === '') return;
        rows += '<div class="tmc-cart__prop"><dt>' + esc(key) + '</dt><dd>' + esc(value) + '</dd></div>';
      });
      return rows ? '<dl class="tmc-cart__props">' + rows + '</dl>' : '';
    }

    /* Two seconds after the read that showed a line as new, its ADDED mark drops away and the row is a plain row (board state B,
       "settles"): the chip goes, the row stays where it is. Marks that have not run out yet get a timer for the earliest. */
    settle() {
      var self = this, now = Date.now(), next = 0;
      Object.keys(this.marks).forEach(function (key) {
        if (self.marks[key] <= now) delete self.marks[key];
        else next = next ? Math.min(next, self.marks[key]) : self.marks[key];
      });
      this.lines.querySelectorAll('.is-new').forEach(function (li) {
        if (self.marks[li.getAttribute('data-line-key')]) return;
        li.classList.remove('is-new');
        var chip = li.querySelector('.tmc-cart__chip');
        if (chip) chip.remove();
      });
      clearTimeout(this.settleTimer);
      if (next) this.settleTimer = setTimeout(function () { self.settle(); }, next - now);
    }

    render(cart, fresh) {
      /* A prior read or write can have left the "could not be loaded"/"could not be made" banner on; this is the
       * one path every successful render shares (refresh, remove, tidy), so clearing it here always reaches the
       * next success, rather than depending on every call site to remember to (NW-35 follow-up, #286). */
      this.error('');
      var self = this, count = 0, tests = '', satchel = '', now = Date.now();
      (cart.items || []).forEach(function (item) {
        var img = item.image
          ? '<img class="tmc-cart__img" src="' + esc(item.image.replace(/(\.[a-z]+)(\?|$)/i, '_112x112$1$2')) + '" alt="" width="56" height="56" loading="lazy">'
          : '<div class="tmc-cart__tile" aria-hidden="true"></div>';

        if (self.isSatchel(item)) {
          satchel = '<li class="tmc-cart__line tmc-cart__line--satchel" data-line-key="' + esc(item.key) + '">' +
            '<div class="tmc-cart__tile" aria-hidden="true">' +
            '<svg viewBox="0 0 22 22"><path d="M2.5 7.2 11 2.6l8.5 4.6v7.6L11 19.4l-8.5-4.6V7.2Z"/><path d="M2.5 7.2 11 11.8l8.5-4.6"/><path d="M11 11.8v7.6"/></svg>' +
            '</div>' +
            '<div class="tmc-cart__info"><p class="tmc-cart__name">' + esc(item.product_title) + '</p>' +
            '<p class="tmc-cart__sub">Added once per order.</p>' +
            '<p class="tmc-cart__tag">Added automatically</p></div>' +
            '<span class="tmc-cart__price">' + money(item.final_line_price) + '</span></li>';
          return;
        }

        count += item.quantity;
        if (fresh && !self.known[item.key] && !self.marks[item.key]) self.marks[item.key] = now + SETTLE_MS;
        var isNew = !!self.marks[item.key] && self.marks[item.key] > now;
        var title = esc(item.product_title) + (item.variant_title && item.variant_title !== 'Default Title' ? ' &middot; ' + esc(item.variant_title) : '');
        var sub = self.lineMap[String(item.variant_id)];
        tests += '<li class="tmc-cart__line tmc-cart__line--test' + (isNew ? ' is-new' : '') + '" data-line-key="' + esc(item.key) + '">' + img +
          '<div class="tmc-cart__info">' + (isNew ? CHIP : '') + '<p class="tmc-cart__name">' + title + '</p>' +
          (sub ? '<p class="tmc-cart__sub">' + esc(sub) + '</p>' : '') +
          self.propsHTML(item) + '</div>' +
          '<span class="tmc-cart__price">' + moneyShort(item.final_line_price) + '</span>' +
          '<button class="tmc-cart__remove" type="button" data-cart-remove aria-label="Remove ' +
          esc(item.product_title) + '">Remove</button></li>';
      });

      var empty = count === 0;
      this.lines.innerHTML = empty ? '' : tests + satchel;
      this.known = {};
      (cart.items || []).forEach(function (item) { self.known[item.key] = true; });
      Object.keys(this.marks).forEach(function (key) { if (!self.known[key]) delete self.marks[key]; });
      this.settle();
      this.classList.toggle('is-empty', empty);
      this.querySelector('[data-cart-empty]').hidden = !empty;
      this.querySelector('[data-cart-empty-foot]').hidden = !empty;
      this.querySelector('[data-cart-foot]').hidden = empty;
      this.querySelector('[data-cart-subtotal]').textContent = moneyShort(cart.total_price || 0);
      this.querySelector('[data-cart-count-label]').textContent =
        count + (count === 1 ? ' item' : ' items') + ' · prices in AUD';
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: count, cart: cart } }));
    }
  }
  customElements.define('cart-drawer', TMCCartDrawer);
})();
